import type { ReactNode } from 'react'

import { BreadcrumbProvider } from './breadcrumb'
import { HeaderBar } from './header-bar'
import { Sidebar } from './sidebar'

/**
 * AppShell - page wrapper for every signed-in route.
 *
 * Layout per the design handoff "Layout / shell" section: a fixed 232px
 * sidebar at full height beside a column of the 56px header bar and a
 * scrollable content area. Only the content area scrolls, so the rail and the
 * header stay put while a long list moves - which is the point of the rail.
 *
 * Content is padded 24px/32px with an 1180px measure centred inside it, and
 * children are stacked with a 16px gap so screens do not each re-state their
 * own vertical rhythm.
 *
 * This supersedes the top-nav-only shell in the MUR "Information Architecture and Navigation" page. See `sidebar.tsx`
 * for why that decision was reversed.
 *
 * Per `doc/design-system.md`:
 *   - `role="banner"` for the header (set on `<HeaderBar>`).
 *   - `role="main"` for the content area (set here as `<main>`).
 *   - Skip-link "Skip to main content" as the first focusable element on
 *     every page (rendered visibly only when focused; see the `.skip-link`
 *     rule in `index.css`).
 *
 * The build/env footer strip is not rendered here - the version, environment
 * and tenant now sit in the sidebar footer, and `AppFooter` carries only the
 * dev JWT panel.
 */
export interface AppShellProps {
  readonly children: ReactNode
}

export function AppShell({ children }: AppShellProps): JSX.Element {
  return (
    <BreadcrumbProvider>
      <div className="flex h-full min-h-0 w-full">
        <a href="#main" className="skip-link sr-only">
          Skip to main content
        </a>
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <HeaderBar />
          <main
            id="main"
            role="main"
            className="flex min-h-0 flex-1 flex-col overflow-y-auto px-murmuration-6 py-murmuration-5"
          >
            <div className="mx-auto flex w-full min-h-0 max-w-content flex-1 flex-col gap-murmuration-4">
              {children}
            </div>
          </main>
        </div>
      </div>
    </BreadcrumbProvider>
  )
}
